import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  Button,
  Card,
  CardContent,
  Chip,
  useTheme,
  useMediaQuery,
  alpha,
  Fade,
  Slide,
  Zoom,
  Dialog,
  DialogContent,
  IconButton
} from '@mui/material';
import {
  ArrowRight,
  X,
  Calendar,
  MapPin,
  Users,
  Building2,
  Target,
  Star,
  Award,
  Lightbulb,
  CheckCircle,
  Eye,
  TrendingUp,
  Briefcase,
  Monitor,
  Coffee,
  Shield
} from 'lucide-react';

// Animated background shape component (same as residential)
const AnimatedShape = ({ top, left, size, delay, color, type }) => {
  const theme = useTheme();
  
  return (
    <Box
      sx={{
        position: 'absolute',
        top: top,
        left: left,
        width: size,
        height: size,
        opacity: 0.1,
        background: color === 'primary' ? theme.palette.primary.main : theme.palette.primary.light,
        borderRadius: type === 'circle' ? '50%' : '30% 70% 70% 30% / 30% 30% 70% 70%',
        animation: type === 'circle' 
          ? `float 10s ease-in-out ${delay}s infinite` 
          : `morphBlob 20s ease-in-out ${delay}s infinite`,
        zIndex: 0,
        filter: 'blur(60px)',
        '@keyframes float': {
          '0%, 100%': { transform: 'translateY(0) rotate(0deg)' },
          '50%': { transform: 'translateY(-20px) rotate(5deg)' }
        },
        '@keyframes morphBlob': {
          '0%, 100%': { borderRadius: '30% 70% 70% 30% / 30% 30% 70% 70%' },
          '25%': { borderRadius: '70% 30% 30% 70% / 70% 70% 30% 30%' },
          '50%': { borderRadius: '30% 70% 30% 70% / 70% 30% 70% 30%' },
          '75%': { borderRadius: '70% 30% 70% 30% / 30% 70% 30% 70%' }
        }
      }}
    />
  );
};

// Project Card Component (similar to residential but with commercial theming)
const ProjectCard = ({ project, delay, onClick }) => {
  const theme = useTheme();
  const [hovered, setHovered] = useState(false);

  return (
    <Fade in={true} style={{ transitionDelay: `${delay}ms` }}>
      <Card 
        sx={{ 
          height: '100%',
          borderRadius: 4,
          overflow: 'hidden',
          position: 'relative',
          transition: 'all 0.3s ease',
          cursor: 'pointer',
          background: theme.customColors.backgrounds.glass,
          backdropFilter: 'blur(10px)',
          border: '1px solid rgba(255,255,255,0.1)',
          '&:hover': {
            transform: 'translateY(-10px)',
            boxShadow: '0 20px 40px rgba(0,0,0,0.3)'
          },
          '&:hover .project-overlay': { opacity: 1 },
          '&:hover .project-image': { transform: 'scale(1.1)' }
        }}
        onClick={() => onClick(project)}
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
      >
        <Box 
          sx={{ 
            position: 'relative', 
            height: 280,
            overflow: 'hidden',
            background: project.gradient,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}
        >
          <Box
            className="project-image"
            sx={{
              display: 'grid',
              gridTemplateColumns: 'repeat(3, 1fr)',
              gap: 3,
              transition: 'transform 0.6s ease',
              p: 4,
              width: '100%',
              height: '100%',
              alignItems: 'center',
              justifyItems: 'center'
            }}
          >
            {project.icons.map((IconComponent, index) => (
              <Box
                key={index}
                sx={{
                  color: 'white',
                  opacity: 0.9,
                  animation: `iconBounce 2s ease-in-out ${index * 0.3}s infinite alternate`,
                  transform: hovered ? `scale(${1.2 + (index % 2) * 0.2})` : `scale(${1 + (index % 2) * 0.1})`,
                  transition: 'transform 0.3s ease',
                  '@keyframes iconBounce': {
                    '0%': { transform: 'translateY(0)' },
                    '100%': { transform: 'translateY(-8px)' }
                  }
                }}
              >
                <IconComponent size={36 + index * 2} />
              </Box>
            ))}
          </Box>
          
          <Box
            className="project-overlay"
            sx={{
              position: 'absolute',
              inset: 0,
              background: theme.customColors.gradients.overlay,
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'flex-end',
              p: 3,
              opacity: 0.8,
              transition: 'opacity 0.3s ease'
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
              <Building2 size={16} style={{ marginRight: 8, color: theme.palette.primary.main }} />
              <Typography variant="overline" color="primary.main" sx={{ fontWeight: 600 }}>
                COMMERCIAL
              </Typography>
            </Box>
            <Typography variant="h6" color="text.primary" gutterBottom sx={{ fontWeight: 600 }}>
              {project.title}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {project.description}
            </Typography>
            <Button 
              variant="outlined"
              size="small"
              endIcon={<Eye size={16} />}
              sx={{ 
                alignSelf: 'flex-start',
                color: 'white',
                borderColor: 'rgba(255,255,255,0.5)',
                '&:hover': { borderColor: 'white', background: 'rgba(255,255,255,0.1)' }
              }}
            >
              View Details
            </Button>
          </Box>
        </Box>
      </Card>
    </Fade>
  );
};

// Project Detail Modal (similar structure)
const ProjectDetailModal = ({ project, open, onClose }) => {
  const theme = useTheme();
  
  if (!project) return null;

  return (
    <Dialog 
      open={open} 
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          background: 'linear-gradient(to bottom, #212121, #000000)',
          borderRadius: 4,
          border: '1px solid rgba(255,255,255,0.1)',
          overflow: 'hidden'
        }
      }}
    >
      <DialogContent sx={{ p: 0 }}>
        <Box sx={{ position: 'relative' }}>
          <IconButton
            onClick={onClose}
            sx={{
              position: 'absolute',
              top: 16,
              right: 16,
              zIndex: 10,
              background: 'rgba(0,0,0,0.8)',
              color: 'white',
              '&:hover': { background: 'rgba(0,0,0,0.9)' }
            }}
          >
            <X size={24} />
          </IconButton>

          <Box
            sx={{
              height: 300,
              background: project.gradient,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              position: 'relative'
            }}
          >
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: 'repeat(4, 1fr)',
                gap: 3,
                p: 4,
                width: '100%',
                height: '100%',
                alignItems: 'center',
                justifyItems: 'center'
              }}
            >
              {[...project.icons, ...project.icons.slice(0, 2)].map((IconComponent, index) => (
                <Box
                  key={index}
                  sx={{
                    color: 'white',
                    opacity: 0.8,
                    animation: `modalIconFloat 3s ease-in-out ${index * 0.2}s infinite alternate`,
                    '@keyframes modalIconFloat': {
                      '0%': { transform: 'translateY(0) rotate(0deg)' },
                      '100%': { transform: 'translateY(-10px) rotate(5deg)' }
                    }
                  }}
                >
                  <IconComponent size={28 + (index % 3) * 6} />
                </Box>
              ))}
            </Box>
          </Box>

          <Box sx={{ p: 4 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <Building2 size={20} style={{ marginRight: 12, color: theme.palette.primary.main }} />
              <Typography variant="overline" color="primary.main" sx={{ fontWeight: 600 }}>
                COMMERCIAL PROJECT
              </Typography>
            </Box>

            <Typography variant="h4" color="text.primary" gutterBottom sx={{ fontWeight: 700 }}>
              {project.title}
            </Typography>

            <Typography variant="body1" color="text.secondary" paragraph>
              {project.fullDescription}
            </Typography>

            <Grid container spacing={3} sx={{ mt: 2 }}>
              <Grid item xs={12} sm={6}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <Calendar size={18} style={{ marginRight: 12, color: theme.palette.primary.main }} />
                  <Box>
                    <Typography variant="body2" color="text.disabled">Completion Date</Typography>
                    <Typography variant="body1" color="text.primary">{project.completionDate}</Typography>
                  </Box>
                </Box>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <MapPin size={18} style={{ marginRight: 12, color: theme.palette.primary.main }} />
                  <Box>
                    <Typography variant="body2" color="text.disabled">Location</Typography>
                    <Typography variant="body1" color="text.primary">{project.location}</Typography>
                  </Box>
                </Box>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <Users size={18} style={{ marginRight: 12, color: theme.palette.primary.main }} />
                  <Box>
                    <Typography variant="body2" color="text.disabled">Area</Typography>
                    <Typography variant="body1" color="text.primary">{project.area}</Typography>
                  </Box>
                </Box>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <Users size={18} style={{ marginRight: 12, color: theme.palette.primary.main }} />
                  <Box>
                    <Typography variant="body2" color="text.disabled">Employees</Typography>
                    <Typography variant="body1" color="text.primary">{project.employees}</Typography>
                  </Box>
                </Box>
              </Grid>
            </Grid>

            {project.features && (
              <Box sx={{ mt: 3 }}>
                <Typography variant="h6" color="text.primary" gutterBottom>Key Features</Typography>
                <Grid container spacing={1}>
                  {project.features.map((feature, index) => (
                    <Grid item key={index}>
                      <Chip
                        label={feature}
                        size="small"
                        sx={{
                          background: alpha(theme.palette.primary.main, 0.2),
                          color: theme.palette.primary.light,
                          border: `1px solid ${alpha(theme.palette.primary.main, 0.3)}`
                        }}
                      />
                    </Grid>
                  ))}
                </Grid>
              </Box>
            )}
          </Box>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

const CommercialPortfolio = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [showElements, setShowElements] = useState(false);
  const [selectedProject, setSelectedProject] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => setShowElements(true), 300);
    return () => clearTimeout(timer);
  }, []);

  const commercialProjects = [
    {
      id: 1,
      title: 'Tech Corporate Headquarters',
      description: 'Modern workspace designed for innovation and collaboration',
      fullDescription: 'A cutting-edge corporate headquarters featuring open collaborative spaces, private focus areas, and state-of-the-art technology integration. The design promotes creativity while maintaining professional sophistication.',
      icons: [Building2, Users, Target, Lightbulb, TrendingUp, Award],
      gradient: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      location: 'HITEC City, Hyderabad',
      completionDate: 'March 2024',
      area: '25,000 sq ft',
      employees: '500+ employees',
      features: ['Open Collaboration', 'Tech Integration', 'Flexible Workspaces', 'Executive Suites', 'Wellness Areas']
    },
    {
      id: 2,
      title: 'Financial Services Office',
      description: 'Professional environment that builds client trust and confidence',
      fullDescription: 'A sophisticated office design for a financial services firm, emphasizing trust, stability, and professionalism while creating comfortable spaces for both employees and client meetings.',
      icons: [Building2, Shield, Star, Target, Award, CheckCircle],
      gradient: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
      location: 'Financial District, Hyderabad',
      completionDate: 'January 2024',
      area: '18,000 sq ft',
      employees: '200+ employees',
      features: ['Client Meeting Rooms', 'Secure Areas', 'Executive Offices', 'Reception Lounge', 'Conference Facilities']
    },
    {
      id: 3,
      title: 'Creative Agency Studio',
      description: 'Inspiring workspace that fuels creativity and innovation',
      fullDescription: 'A vibrant creative studio designed to inspire artistic expression and innovation. Features include flexible presentation spaces, collaborative work areas, and zones designed for different creative processes.',
      icons: [Building2, Lightbulb, Target, Star, Eye, TrendingUp],
      gradient: 'linear-gradient(135deg, #a8edea 0%, #fed6e3 100%)',
      location: 'Banjara Hills, Hyderabad',
      completionDate: 'February 2024',
      area: '12,000 sq ft',
      employees: '150+ employees',
      features: ['Creative Studios', 'Presentation Theater', 'Brainstorming Areas', 'Art Gallery', 'Flexible Layouts']
    },
    {
      id: 4,
      title: 'Medical Practice Office',
      description: 'Healing environment focused on patient comfort and care',
      fullDescription: 'A medical office designed with patient wellness in mind, featuring calming colors, natural light, and efficient workflow for medical staff while ensuring privacy and comfort for patients.',
      icons: [Building2, Shield, CheckCircle, Star, Target, Users],
      gradient: 'linear-gradient(135deg, #ffecd2 0%, #fcb69f 100%)',
      location: 'Jubilee Hills, Hyderabad',
      completionDate: 'December 2023',
      area: '8,000 sq ft',
      employees: '50+ staff',
      features: ['Patient Comfort', 'Private Consultation', 'Efficient Workflow', 'Waiting Areas', 'Medical Equipment Integration']
    },
    {
      id: 5,
      title: 'Co-working Space',
      description: 'Flexible workspace for entrepreneurs and startups',
      fullDescription: 'A dynamic co-working space designed to support entrepreneurs and small businesses. Features include hot desks, private offices, meeting rooms, and networking areas that foster collaboration.',
      icons: [Building2, Users, Lightbulb, Coffee, Target, TrendingUp],
      gradient: 'linear-gradient(135deg, #ff9a9e 0%, #fecfef 100%)',
      location: 'Gachibowli, Hyderabad',
      completionDate: 'November 2023',
      area: '15,000 sq ft',
      employees: '300+ members',
      features: ['Flexible Desks', 'Private Offices', 'Meeting Rooms', 'Networking Lounge', 'Event Space']
    },
    {
      id: 6,
      title: 'Manufacturing Office',
      description: 'Industrial office combining functionality with modern design',
      fullDescription: 'An office complex for a manufacturing company that bridges the gap between industrial functionality and modern workplace design, featuring both administrative and operational areas.',
      icons: [Building2, Target, Shield, CheckCircle, Award, Monitor],
      gradient: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      location: 'Outer Ring Road, Hyderabad',
      completionDate: 'October 2023',
      area: '20,000 sq ft',
      employees: '400+ employees',
      features: ['Admin Offices', 'Operations Center', 'Safety Compliance', 'Training Rooms', 'Quality Control']
    }
  ];

  const handleProjectClick = (project) => {
    setSelectedProject(project);
    setModalOpen(true);
  };

  return (
    <Box sx={{ overflow: 'hidden' }}>
      {/* Hero Section */}
      <Box 
        sx={{ 
          minHeight: '60vh',
          display: 'flex',
          alignItems: 'center',
          position: 'relative',
          background: theme.palette.background.default,
          pt: { xs: 15, md: 20 },
          pb: { xs: 10, md: 15 },
          overflow: 'hidden'
        }}
      >
        <AnimatedShape top="10%" left="5%" size="300px" delay={0} color="primary" type="blob" />
        <AnimatedShape top="60%" left="80%" size="200px" delay={0.5} color="primary" type="circle" />
        <AnimatedShape top="30%" left="60%" size="350px" delay={1} color="secondary" type="blob" />
        
        <Container maxWidth="lg">
          <Grid container spacing={8} alignItems="center">
            <Grid item xs={12} md={8}>
              <Fade in={showElements} timeout={1000}>
                <Box>
                  <Box 
                    sx={{ 
                      display: 'inline-block', 
                      px: 2, 
                      py: 0.5, 
                      bgcolor: theme.customColors.backgrounds.glass,
                      backdropFilter: 'blur(4px)',
                      borderRadius: 5,
                      mb: 3,
                      border: '1px solid rgba(255,255,255,0.1)'
                    }}
                  >
                    <Typography variant="subtitle2" color="primary.main">
                      COMMERCIAL PORTFOLIO
                    </Typography>
                  </Box>
                  
                  <Typography 
                    variant="h1" 
                    component="h1" 
                    color="text.primary"
                    sx={{ 
                      fontSize: { xs: '2.5rem', md: '3.5rem', lg: '4rem' },
                      mb: 3 
                    }}
                  >
                    Building{' '}
                    <Box component="span" sx={{ position: 'relative' }}>
                      <Box component="span" color="primary.main">Success</Box>
                      <Box
                        component="span"
                        sx={{
                          position: 'absolute',
                          left: 0,
                          right: 0,
                          bottom: { xs: -4, md: -8 },
                          height: 4,
                          backgroundColor: 'primary.main',
                          borderRadius: 2
                        }}
                      />
                    </Box>
                    <Box component="span" display="block" mt={1}>
                      Through{' '}
                      <Box component="span" color="primary.main">Design</Box>
                    </Box>
                  </Typography>
                  
                  <Typography 
                    variant="h6" 
                    component="p" 
                    color="text.secondary"
                    sx={{ maxWidth: 600, mb: 5 }}
                  >
                    Explore our commercial projects that enhance productivity, inspire teams, 
                    and drive business success through thoughtful workplace design.
                  </Typography>
                  
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 4, alignItems: 'center' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <Box sx={{ 
                        width: 48, 
                        height: 48, 
                        background: theme.customColors.gradients.primary,
                        borderRadius: '50%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        mr: 2
                      }}>
                        <Building2 size={24} color="white" />
                      </Box>
                      <Typography color="text.primary">
                        {commercialProjects.length} Business Spaces
                      </Typography>
                    </Box>
                    
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <Box sx={{ 
                        width: 48, 
                        height: 48, 
                        background: theme.customColors.gradients.primary,
                        borderRadius: '50%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        mr: 2
                      }}>
                        <TrendingUp size={24} color="white" />
                      </Box>
                      <Typography color="text.primary">
                        Productivity-Focused Design
                      </Typography>
                    </Box>
                  </Box>
                </Box>
              </Fade>
            </Grid>
            <Grid item xs={12} md={4} sx={{ display: { xs: 'none', md: 'block' } }}>
              <Slide direction="left" in={showElements} timeout={1000}>
                <Box
                  sx={{
                    position: 'relative',
                    height: 400,
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center'
                  }}
                >
                  <Box
                    sx={{
                      position: 'absolute',
                      top: '50%',
                      left: '50%',
                      transform: 'translate(-50%, -50%)',
                      width: '80%',
                      height: '80%',
                      borderRadius: 4,
                      background: theme.customColors.gradients.primary,
                      opacity: 0.3,
                      filter: 'blur(20px)',
                      animation: 'pulse 5s infinite alternate',
                    }}
                  />
                  
                  <Box
                    sx={{
                      display: 'grid',
                      gridTemplateColumns: 'repeat(3, 1fr)',
                      gap: 3,
                      width: '100%',
                      maxWidth: 240,
                      zIndex: 2
                    }}
                  >
                    {[Building2, Users, Target, Briefcase, TrendingUp, Monitor, Lightbulb, Award, CheckCircle].map((IconComponent, index) => (
                      <Zoom key={index} in={true} style={{ transitionDelay: `${index * 100}ms` }}>
                        <Box
                          sx={{
                            width: 50,
                            height: 50,
                            borderRadius: '12px',
                            background: theme.customColors.gradients.primary,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            color: 'white',
                            boxShadow: theme.customColors.shadows.primary,
                            animation: `iconFloat 3s ease-in-out ${index * 0.1}s infinite alternate`,
                            '@keyframes iconFloat': {
                              '0%': { transform: 'translateY(0)' },
                              '100%': { transform: 'translateY(-8px)' }
                            }
                          }}
                        >
                          <IconComponent size={24} />
                        </Box>
                      </Zoom>
                    ))}
                  </Box>
                </Box>
              </Slide>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Projects Grid */}
      <Box
        sx={{
          py: { xs: 10, md: 15 },
          position: 'relative',
          overflow: 'hidden',
          background: theme.customColors.gradients.background1
        }}
      >
        <AnimatedShape top="20%" right="5%" size="250px" delay={0.2} color="primary" type="blob" />
        <AnimatedShape top="70%" left="10%" size="200px" delay={0.8} color="secondary" type="circle" />
        
        <Container maxWidth="lg">
          <Box sx={{ textAlign: 'center', mb: 8 }}>
            <Fade in={showElements}>
              <Box>
                <Typography variant="h2" component="h2" color="text.primary" sx={{ mb: 2 }}>
                  Commercial Projects
                </Typography>
                <Typography variant="h6" component="p" color="text.secondary" sx={{ maxWidth: 700, mx: 'auto' }}>
                  Workspaces designed to inspire teams and drive business success.
                </Typography>
              </Box>
            </Fade>
          </Box>
          
          <Grid container spacing={4}>
            {commercialProjects.map((project, index) => (
              <Grid item xs={12} sm={6} lg={4} key={project.id}>
                <ProjectCard 
                  project={project} 
                  delay={index * 200}
                  onClick={handleProjectClick}
                />
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* CTA Section */}
      <Box
        sx={{
          py: { xs: 10, md: 15 },
          position: 'relative',
          overflow: 'hidden',
          background: `linear-gradient(135deg, ${alpha(theme.palette.primary.dark, 0.9)}, ${alpha(theme.palette.secondary.main, 0.95)})`
        }}
      >
        <Container maxWidth="md">
          <Fade in={showElements}>
            <Box sx={{ textAlign: 'center' }}>
              <Typography variant="h2" component="h2" color="text.primary" sx={{ mb: 3 }}>
                Ready to Transform Your Workplace?
              </Typography>
              <Typography variant="h5" component="p" color="text.primary" sx={{ mb: 6, opacity: 0.9 }}>
                Let's create a workspace that drives productivity and reflects your company culture.
              </Typography>
              <Button
                href="/contact"
                variant="contained"
                size="large"
                endIcon={<ArrowRight />}
                sx={{
                  background: 'rgba(255,255,255,0.2)',
                  backdropFilter: 'blur(10px)',
                  border: '1px solid rgba(255,255,255,0.3)',
                  color: 'white',
                  '&:hover': { background: 'rgba(255,255,255,0.3)' }
                }}
              >
                Start Your Business Project
              </Button>
            </Box>
          </Fade>
        </Container>
      </Box>

      {/* Project Detail Modal */}
      <ProjectDetailModal 
        project={selectedProject}
        open={modalOpen}
        onClose={() => setModalOpen(false)}
      />
    </Box>
  );
};

export default CommercialPortfolio;